import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

export default function ExerciseDetailsModal({ exercise, open, onOpenChange }) {
  if (!exercise) return null;

  const gif = exercise.gifUrl || exercise.gif_url || exercise.media || exercise.video_url || null;

  // Robustly extract instructions as an array of step strings
  let instructionsList = [];
  if (Array.isArray(exercise.instructions) && exercise.instructions.length > 0) {
    instructionsList = exercise.instructions;
  } else if (typeof exercise.instructions === "string" && exercise.instructions.trim()) {
    try {
      const parsed = JSON.parse(exercise.instructions);
      if (Array.isArray(parsed)) instructionsList = parsed;
      else instructionsList = [exercise.instructions];
    } catch {
      instructionsList = exercise.instructions.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    }
  } else if (exercise.description && typeof exercise.description === "string") {
    instructionsList = exercise.description.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  }

  // Strip redundant leading "Step:1 ", "Step 1: ", "1. " from individual steps
  const cleanStep = (step) => {
    if (typeof step !== "string") return String(step || "");
    return step.replace(/^(?:Step\s*[:#]?\s*\d+\s*[:\-.]?\s*|\d+[\.\):]\s*)/i, "").trim();
  };

  const equipments = Array.isArray(exercise.equipments)
    ? exercise.equipments
    : exercise.equipment
    ? [exercise.equipment]
    : [];

  const targetMuscles = Array.isArray(exercise.targetMuscles)
    ? exercise.targetMuscles
    : typeof exercise.muscles_primary === "string"
    ? exercise.muscles_primary.split(/,\s*/).filter(Boolean)
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-lg sm:max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6 rounded-2xl sm:rounded-lg">
        <DialogHeader>
          <DialogTitle className="capitalize font-display text-xl">{exercise.name}</DialogTitle>
          {(equipments.length > 0 || targetMuscles.length > 0) && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {targetMuscles.map((m) => (
                <Badge key={m} variant="secondary" className="text-[10px] capitalize bg-primary/10 text-primary">
                  {m}
                </Badge>
              ))}
              {equipments.map((eq) => (
                <Badge key={eq} variant="outline" className="text-[10px] capitalize border-amber-500/20 text-amber-400 bg-amber-500/5">
                  {eq}
                </Badge>
              ))}
            </div>
          )}
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {gif && (
            <div className="rounded-xl bg-black/40 border border-white/10 flex items-center justify-center h-52 sm:h-80 overflow-hidden">
              <img
                src={gif}
                alt={exercise.name}
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
          )}

          <div className="space-y-3">
            {(exercise.sets || exercise.reps || exercise.rest_seconds) && (
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm font-semibold mb-2">Details</p>
                <div className="flex gap-4 text-sm">
                  {exercise.sets && <div><span className="text-muted-foreground">Sets:</span> {exercise.sets}</div>}
                  {exercise.reps && <div><span className="text-muted-foreground">Reps:</span> {exercise.reps}</div>}
                  {exercise.rest_seconds && <div><span className="text-muted-foreground">Rest:</span> {exercise.rest_seconds}s</div>}
                </div>
              </div>
            )}

            {instructionsList.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">How to Perform</p>
                <ol className="space-y-2">
                  {instructionsList.map((instruction, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex gap-3 leading-relaxed">
                      <span className="font-semibold text-primary min-w-6">{i + 1}.</span>
                      <span>{cleanStep(instruction)}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {exercise.notes && (
              <div>
                <p className="text-sm font-semibold mb-2">Notes</p>
                <p className="text-sm text-muted-foreground">{exercise.notes}</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}